import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { inject, injectable } from 'inversify';
import { ClientTypes as CommonTypes } from '@common-stack/client-core';
import { IQuickInputService, IPickItem, IPickInputOptions, IInputOptions, CacheTypenames, selectedFragmentDoc,
} from '@workbench-stack/core';
import { QuickInputModelDocument } from '@workbench-stack/components/lib/editors';

@injectable()
export class QuickInputService implements IQuickInputService {

    constructor(
        @inject(CommonTypes.ApolloClient)
        protected client: ApolloClient<any>,
        @inject(CommonTypes.InMemoryCache)
        protected cache: InMemoryCache,
    ) {
    }

    public async cancel(): Promise<void> {
        const data = {
            show: false,
            input: null,
            quickPick: null,
            __typename: CacheTypenames.QuickInputModel,
        };

        try {
            await this.client.writeQuery({
                query: QuickInputModelDocument,
                data: { quickInputModel: data },
            });
        } catch (e) {
            console.error('close model is failed', e);
        }
        return Promise.resolve(undefined);
    }

    public async input(options?: IInputOptions): Promise<string | undefined> {
        const inputData = {
            title: options.title,
            description: null,
            enabled: true,
            ignoreFocusOut: true,
            value: options.value || '',
            placeholder: options.placeHolder || '',
            prompt: options.prompt || '',
            validationMessage: options.validationMessage,
            __typename: CacheTypenames.QuickInput,
        };
        const data = {
            show: true,
            input: inputData,
            quickPick: null,
            __typename: CacheTypenames.QuickInputModel,
        };

        try {
            await this.client.writeQuery({
                query: QuickInputModelDocument,
                data: { quickInputModel: data },
            });
            await this.client.writeFragment({
                fragment: selectedFragmentDoc,
                id: CacheTypenames.QuickInputModel,
                data: {
                    title: options.title,
                    selectedValue: null,
                    inputValue: null,
                    __typename: CacheTypenames.QuickInputModel,
                },
                fragmentName: 'selected',
            });
            return 'open input-model';
        } catch (e) {
            console.error('open input model is failed', e);
            return e.toString();
        }
    }

    public async pick(picks: IPickItem[], options?: IPickInputOptions): Promise<boolean> {
        const inputData = {
            title: options.title,
            description: null,
            enabled: true,
            ignoreFocusOut: true,
            value: '',
            placeholder: options.placeHolder || '',
            prompt: null,
            validationMessage: null,
            __typename: CacheTypenames.QuickInput,
        };
        const selectedItems = picks.filter((item) => item.picked);
        const activeItem = options.activeItem || selectedItems[0];
        const quickPick = {
            placeholder: null,
            canSelectMany: options.canPickMany,
            autoFocusOnList: true,
            items: picks.map((item) => ({ ...item, __typename: CacheTypenames.QuickPickItem })),
            activeItem: { ...activeItem, __typename: CacheTypenames.QuickPickItem },
            selectedItems: selectedItems.map((item) => ({ ...item, __typename: CacheTypenames.QuickPickItem })),
            __typename: CacheTypenames.QuickPick,
        };
        const data = {
            show: true,
            input: inputData,
            quickPick: quickPick,
            __typename: CacheTypenames.QuickInputModel,
        };

        try {
            await this.client.writeQuery({
                query: QuickInputModelDocument,
                data: { quickInputModel: data },
            });
            await this.client.writeFragment({
                fragment: selectedFragmentDoc,
                id: CacheTypenames.QuickInputModel,
                data: {
                    title: options.title,
                    selectedValue: null,
                    inputValue: null,
                    __typename: CacheTypenames.QuickInputModel,
                },
                fragmentName: 'selected',
            });
            return true;
        } catch (e) {
            console.error('open input model is failed', e);
            return false;
        }
    }

    public async setSelected(selectedValue: IPickItem[], inputValue: string): Promise<boolean> {
        let data;
        try {
            data = await this.client.readFragment({
                fragment: selectedFragmentDoc,
                id: CacheTypenames.QuickInputModel,
                fragmentName: 'selected',
            });
        } catch (e) {
            console.error('read selected fragment failed ', e);
        }
        await this.client.writeFragment({
            fragment: selectedFragmentDoc,
            id: CacheTypenames.QuickInputModel,
            data: {
                title: data.title,
                selectedValue,
                inputValue,
                __typename: CacheTypenames.QuickInputModel
            },
            fragmentName: 'selected',
        });
        return true;
    }
}
